/**
 * Thin typed client for the backend. Centralises auth-aware fetch + error
 * handling so route handlers don't repeat 401-handling boilerplate.
 *
 * 2026-05-03 — per-eSIM balance model. Removed: fetchBalance, /api/wallet/topup
 * payload (now goes through /api/esims and /api/esims/:id/topup). History
 * fetcher now hits /api/me/history (consolidated across all of user's eSIMs).
 */
import { Auth } from './auth';
import { showToast } from './toast';
import { t } from './i18n';

export class ApiError extends Error {
  constructor(public readonly status: number, message: string, public readonly body?: unknown) {
    super(message);
  }
}

/**
 * A 401 re-checks the session through Auth.init(). If /api/me still says
 * "signed in" while the endpoint keeps answering 401, that re-check re-renders
 * the dashboard, which calls the endpoint again: measured 2026-09-29 at ~330
 * requests a second from one tab, forever. At most one re-check per window;
 * inside it the 401 surfaces as an ordinary error.
 */
const REAUTH_WINDOW_MS = 10_000;
let lastReauthAt = -Infinity;

async function jsonFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
    ...init,
  });
  if (res.status === 204) return undefined as T;
  let body: unknown;
  try { body = await res.json(); } catch { body = null; }
  if (!res.ok) {
    if (res.status === 401 && Date.now() - lastReauthAt > REAUTH_WINDOW_MS) {
      lastReauthAt = Date.now();
      void Auth.init();
      showToast(t('toast.sessionExpired'), 'info');
    }
    throw new ApiError(res.status, `HTTP ${res.status}`, body);
  }
  return body as T;
}

/* ─── Consolidated user history ──────────────────────────── */

export interface LedgerEntryResp {
  id: string;
  walletId?: string;       // legacy field, may be empty for new entries
  esimId?: string | null;  // new field, set on per-eSIM ledger entries
  type: string;
  amountMicroUsdt: number;
  balanceAfterMicroUsdt: number;
  refType?: string | null;
  refId?: string | null;
  countryCode?: string | null;  // ISO-2 for usage rows (RU, US, ...) — drives flag emoji rendering
  notes?: string;
  createdAt: string;
}
export interface LedgerHistoryResp {
  entries: LedgerEntryResp[];
  nextCursor: string | null;
}
/** Consolidated history across all of user's eSIMs. */
export function fetchHistoryWithPeriod(opts: { period?: string; cursor?: string; limit?: number }): Promise<LedgerHistoryResp> {
  const qs = new URLSearchParams();
  if (opts.period) qs.set('period', opts.period);
  if (opts.cursor) qs.set('cursor', opts.cursor);
  qs.set('limit', String(opts.limit ?? 20));
  return jsonFetch(`/api/me/history?${qs.toString()}`);
}
/** Per-eSIM history. Supports the same `period` filter as /api/me/history. */
export function fetchEsimHistory(
  esimId: string,
  opts: { period?: string; cursor?: string; limit?: number } = {},
): Promise<LedgerHistoryResp> {
  const qs = new URLSearchParams();
  if (opts.period) qs.set('period', opts.period);
  if (opts.cursor) qs.set('cursor', opts.cursor);
  qs.set('limit', String(opts.limit ?? 20));
  return jsonFetch(`/api/esims/${esimId}/history?${qs.toString()}`);
}

/* ─── eSIMs ──────────────────────────────────────────────── */

export interface EsimResp {
  id: string;
  /** 10-digit MSISDN — primary user-facing ID since 2026-05-08. Same value
   *  the RSP propagates into the eSIM label on the device, so the user can
   *  match what they see in iOS/Android settings to what we show in the
   *  dashboard. Null while RSP hasn't assigned a number yet (rare; UI
   *  shows "Pending..." in that window). Labelled "eSIM ID" / "ID eSIM"
   *  to the user — they don't see the word "MSISDN" anywhere. */
  msisdn: string | null;
  /** Legacy 10-digit auto-generated ID kept for retro reconciliation
   *  (audit log, NowPayments order_description fallback). Hidden from
   *  user-facing UI since 2026-05-08; only surfaces in admin. */
  displayId: string | null;
  /** LEGACY: country fields. With the global eSIM model, the dashboard ignores
   *  these and renders every eSIM as "Global eSIM". Country slug records which
   *  marketing page the click came from (informational only). */
  countrySlug: string;
  countryCode: string;
  countryName: string;
  flag: string;
  ratePerMicroUsdt: number;
  consumedMb: number;
  /** Total amount spent on traffic so far, micro-USDT. Sum of `usage`-type
   * ledger entries for this eSIM. Source of truth for the dashboard's
   * SPENT card (the older `consumedMb × ratePerMicroUsdt` formula breaks
   * on global eSIMs where rate is per-country, not per-eSIM). Optional
   * for backwards compatibility with cached client builds; new builds
   * always receive a number from `/api/esims`. */
  spentMicroUsdt?: number;
  /** Per-eSIM balance (replaces the old per-user wallet model). */
  balanceMicroUsdt: number;
  status: 'reserved' | 'active' | 'paused' | 'archived' | 'released';
  /** Reservation deadline ISO timestamp — non-null only while status='reserved'. */
  reservedUntil: string | null;
  provider: 'stub' | 'venta';
  installedAt: string | null;
  lastSeenAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** Only present on GET /api/esims/:id. */
  qrPayload?: string;
  lpaUri?: string;
  /** When status='reserved' AND a waiting payment_intent exists, this is
   *  the NowPayments invoice URL where user can complete the payment.
   *  Surfaced by the backend so the dashboard can show a "Pay now" CTA
   *  on the reservation card. */
  pendingInvoiceUrl?: string;
  /** Reserved, and NowPayments already reports the transfer on chain
   *  (confirming/confirmed/sending). The server holds the reservation past its
   *  deadline until the payment lands; the card says "confirming". */
  paymentInFlight?: boolean;
  /** User-editable nickname (mig 0025). Null = no label, dashboard shows
   *  "Global eSIM" by default. Editable via PATCH /api/esims/:id. */
  label?: string | null;
  /** ISO Alpha-2 of the country where the last usage row was recorded.
   *  Surfaced only on GET /api/esims/:id (not in the list response).
   *  Null when the user hasn't roamed yet. */
  lastUsageCountryCode?: string | null;
  /** Timestamp of the most recent usage ledger row. Used in the dashboard
   *  status modal to render "Last seen: 5 минут назад". */
  lastUsageAt?: string | null;
  /** Authoritative SIM profile status from the RSP platform (EsimPlatform).
   *  String values seen: "RELEASED", "INSTALLED", "ENABLED", "DISABLED".
   *  Null when RSP is not reachable / not configured — frontend then
   *  derives the visual status from installed_at / last_seen_at. */
  platformStatus?: string | null;
  /** Device the user actually downloaded the eSIM onto, captured on the
   *  RSP side. E.g. "Apple iPhone 15". Useful for support: operator can
   *  see exactly which device a user is asking about. Null when RSP
   *  unavailable or vendor/model not yet recorded. */
  platformDevice?: string | null;
}

/* ─── Rename eSIM (PATCH /api/esims/:id with `label`) ───── */
export function renameEsim(id: string, label: string | null): Promise<{ esim: EsimResp }> {
  return jsonFetch(`/api/esims/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify({ label }),
  });
}

export function fetchEsims(): Promise<{ esims: EsimResp[] }> {
  return jsonFetch('/api/esims');
}

/**
 * Create + reserve a new eSIM, then return a NowPayments invoice URL. The
 * eSIM is pre-created with status='reserved' and `reservedUntil = now+30min`;
 * if payment confirms within the window the IPN flips it to 'active', if not
 * the cleanup loop releases it back to the pool.
 *
 * `email` is only saved if `users.email` is currently null (Telegram-signup
 * users who haven't set one yet).
 */
export interface CreateEsimResp {
  invoiceId: string;
  invoiceUrl: string;
  intentId: string;
  orderId: string;
  /** Reserved eSIM id (already exists in DB at this point, status='reserved'). */
  esimId: string;
  /** 10-digit display id embedded in the NowPayments order_description. */
  displayId: string;
  /** Reservation deadline (ISO). Frontend renders a countdown for the user. */
  reservedUntil?: string;
}
export interface CreateEsimInput {
  /** Optional informational slug — which marketing page the click came from. */
  countrySlug?: string;
  amountUsdt: number;
  payCurrency?: string;
  /** Optional — only persisted if user.email is null (Telegram-only signup). */
  email?: string;
  /** Optional promo code (case-insensitive). Validated server-side. */
  promoCode?: string;
}
export function createEsim(input: CreateEsimInput): Promise<CreateEsimResp> {
  const body: Record<string, unknown> = { amountUsdt: input.amountUsdt };
  if (input.countrySlug) body.countrySlug = input.countrySlug;
  if (input.payCurrency) body.payCurrency = input.payCurrency;
  if (input.email) body.email = input.email;
  if (input.promoCode) body.promoCode = input.promoCode;
  return jsonFetch('/api/esims', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Top up an existing eSIM. Returns invoice URL; balance lands on IPN. */
export interface TopUpInput {
  amountUsdt: number;
  payCurrency?: string;
  email?: string;
  promoCode?: string;
}
export function topUpEsim(esimId: string, input: TopUpInput): Promise<CreateEsimResp> {
  const body: Record<string, unknown> = { amountUsdt: input.amountUsdt };
  if (input.payCurrency) body.payCurrency = input.payCurrency;
  if (input.email) body.email = input.email;
  if (input.promoCode) body.promoCode = input.promoCode;
  return jsonFetch(`/api/esims/${esimId}/topup`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/* ─── Promo validation (POST /api/promo/validate) ─────────── */

export interface PromoValidateResp {
  ok: boolean;
  reason?: string;
  bonusMicroUsdt?: number;
  bonusUsdt?: number;
}
export function validatePromo(code: string, amountUsdt: number): Promise<PromoValidateResp> {
  return jsonFetch('/api/promo/validate', {
    method: 'POST',
    body: JSON.stringify({ code, amountUsdt }),
  });
}

export function patchEsimStatus(id: string, status: EsimResp['status']): Promise<{ esim: EsimResp }> {
  return jsonFetch(`/api/esims/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

export function cancelEsim(id: string): Promise<void> {
  return jsonFetch(`/api/esims/${id}`, { method: 'DELETE' });
}
/* ─── Per-eSIM country breakdown ─────────────────────────── */

/**
 * Per-eSIM consumption by country, over the last N days. Aggregates
 * ledger.usage entries (set by the billing pipeline when Venta lands).
 * Returns empty in dev (stub provider doesn't emit usage).
 */
export interface UsageByCountryResp {
  esimId: string;
  days: number;
  breakdown: Array<{ countryCode: string; mb: number; micros: number }>;
}
export function fetchUsageByCountry(esimId: string, days: 7 | 14 | 30 | 90 = 30): Promise<UsageByCountryResp> {
  return jsonFetch(`/api/esims/${esimId}/usage-by-country?days=${days}`);
}

/* ─── Personal stats (GET /api/me/stats) ─────────────────── */

export interface MeStatsResp {
  days: number;
  empty: boolean;
  esimCount: number;
  activeEsimCount: number;
  reservedEsimCount: number;
  currentBalanceMicroUsdt: number;
  dataConsumedMb: number;
  totalSpentMicroUsdt: number;
  totalToppedUpMicroUsdt: number;
  totalBonusMicroUsdt: number;
  byCountry: Array<{ countryCode: string; microUsdt: number; mb: number }>;
  dailySeries: Array<{ date: string; spentMicroUsdt: number; toppedUpMicroUsdt: number }>;
  recentLedger: Array<{ id: string; esimId: string | null; type: string; amountMicroUsdt: number; createdAt: string; notes: string | null }>;
  /** All of the user's non-released eSIMs — for the analytics picker. */
  availableEsims: Array<{ id: string; displayId: string | null; msisdn: string | null; status: string }>;
  /** Echoes back the active filter so the picker UI stays in sync. */
  selectedEsimId: string | null;
}
export function fetchMeStats(days: 7 | 30 | 90 = 30, esimId?: string): Promise<MeStatsResp> {
  const qs = new URLSearchParams({ days: String(days) });
  if (esimId) qs.set('esimId', esimId);
  return jsonFetch(`/api/me/stats?${qs.toString()}`);
}

/* ─── Referral (mig 0048) ────────────────────────────────── */

export interface ReferralSummaryResp {
  code: string;
  link: string;
  percent: number;
  balanceMicroUsdt: number;
  invitedCount: number;
  earnedTotalMicroUsdt: number;
  canWithdraw: boolean;
  minWithdrawalMicroUsdt: number;
  networks: string[];
  recent: Array<{ type: string; amountMicroUsdt: number; createdAt: string; voided: boolean; notes: string | null }>;
  withdrawals: Array<{ id: string; amountMicroUsdt: number; network: string; address: string; status: string; createdAt: string }>;
}
export function fetchReferral(): Promise<ReferralSummaryResp> {
  return jsonFetch('/api/me/referral');
}
export function attributeReferral(code: string): Promise<{ attributed: boolean; reason?: string }> {
  return jsonFetch('/api/me/referral/attribute', { method: 'POST', body: JSON.stringify({ code }) });
}
export function transferReferralToEsim(esimId: string, amountMicroUsdt: number): Promise<{ ok: true; balanceAfterMicroUsdt: number }> {
  return jsonFetch('/api/me/referral/transfer', { method: 'POST', body: JSON.stringify({ esimId, amountMicroUsdt }) });
}
export function requestReferralWithdrawal(amountMicroUsdt: number, address: string, network: string): Promise<{ ok: true; withdrawalId: string; balanceAfterMicroUsdt: number }> {
  return jsonFetch('/api/me/referral/withdraw', { method: 'POST', body: JSON.stringify({ amountMicroUsdt, address, network }) });
}

/* ─── Account profile (PATCH /api/me) ────────────────────── */

export interface MeResp {
  user: {
    id: string;
    displayName: string;
    email: string | null;
  };
}
/** Update the user's email (e.g. Settings → editable email field). */
export function patchMeEmail(email: string | null): Promise<MeResp> {
  return jsonFetch('/api/me', {
    method: 'PATCH',
    body: JSON.stringify({ email }),
  });
}
